import React, { useState, useEffect, useContext } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import moment from 'moment-timezone';
import './ClockInAndOut.css';
import Footer from '../../../Footer';
import { StateContext } from '../../../StateProvider';
import { BackButton } from '../../../Utilities/components/Buttons';
import { fetchStaffShifts, createShift, updateShift } from '../../../Utilities/api';

const ConfirmationPopup = ({ message, onConfirm, onCancel }) => (
  <div className="popup-content">
    <p className="profile-popup-header">{message}</p>
    <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
      <button className="profile-popup-yes-button" onClick={onConfirm}>Yes</button>
      <button className="profile-popup-no-button" onClick={onCancel}>No</button>
    </div>
  </div>
);

const ClockInAndOut = () => {
  const localTZ = moment.tz.guess();
  const { user } = useContext(StateContext);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [clockOutTime, setClockOutTime] = useState(null);
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState(moment.tz(localTZ).startOf('day').toDate());
  const [shifts, setShifts] = useState([]);
  const [selectedDateShifts, setSelectedDateShifts] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(null);

  useEffect(() => {
    async function loadShifts() {
      if (!user || !user.id) return;

      try {
        const res = await fetchStaffShifts(user.id);
        const rawShifts = Array.isArray(res) ? res : [res];

        const processed = rawShifts.map((s) => {
          const localClockIn = moment.utc(s.clockIn).tz(localTZ);
          const localClockOut = s.clockOut ? moment.utc(s.clockOut).tz(localTZ) : null;
          return { ...s, clockIn: localClockIn, clockOut: localClockOut };
        });

        setShifts(processed);

        // ✅ Only check if user has clocked in today (in local time)
        const todayStart = moment.tz(localTZ).startOf('day');
        const todayEnd = moment.tz(localTZ).endOf('day');

        const todaysOpenShift = processed.find((s) =>
          s.userId === user.id &&
          s.clockIn.isBetween(todayStart, todayEnd, null, '[]') &&
          !s.clockOut
        );

        if (todaysOpenShift) {
          setIsClockedIn(true);
          setClockInTime(todaysOpenShift.clockIn);
          setClockOutTime(null);

          // 🧠 KEY FIX: force calendar to reflect local clockIn date
          const correctedDate = todaysOpenShift.clockIn.clone().startOf('day').toDate();
          setDate(correctedDate);
        }

      } catch (err) {
        console.error('Failed to fetch shifts:', err);
      }
    }

    loadShifts();
  }, [user]);

  useEffect(() => {
    if (!Array.isArray(shifts)) return;

    const dayStart = moment.tz(date, localTZ).startOf('day');
    const dayEnd = moment.tz(date, localTZ).endOf('day');

    const filtered = shifts.filter((shift) =>
      shift.clockIn.isBetween(dayStart, dayEnd, null, '[]')
    );

    setSelectedDateShifts(filtered);
  }, [shifts, date]);

  async function confirmClockIn() {
    const localNow = moment.tz(moment(), localTZ);
    const nowUTC = localNow.utc().toISOString();
    const today = localNow.format('YYYY-MM-DD');

    try {
      const newShift = await createShift({
        userId: user.id,
        date: today,
        clockIn: nowUTC,
        notes: '',
        type: 'work',
      });

      setClockInTime(localNow);
      setClockOutTime(null);
      setIsClockedIn(true);
      const updated = shifts.slice();
      updated.push({
        ...newShift,
        clockIn: localNow,
        clockOut: null,
      });
      setShifts(updated);
    } catch (err) {
      console.error('Clock in failed:', err);
    }

    setShowPopup(false);
  }

  async function confirmClockOut() {
    const localNow = moment.tz(moment(), localTZ);
    const nowUTC = localNow.utc().toISOString();
    const today = localNow.format('YYYY-MM-DD');
    let activeShift = null;

    for (let i = 0; i < shifts.length; i++) {
      const shift = shifts[i];
      const shiftDate = shift.clockIn.format('YYYY-MM-DD');
      if (shift.userId === user.id && shiftDate === today && !shift.clockOut) {
        activeShift = shift;
        break;
      }
    }

    if (!activeShift) {
      console.error('No active shift found to clock out.');
      return;
    }

    try {
      const updated = await updateShift(activeShift.id, {
        clockOut: nowUTC,
        notes: notes,
      });

      const updatedList = [];
      for (let i = 0; i < shifts.length; i++) {
        const s = shifts[i];
        if (s.id === activeShift.id) {
          updatedList.push({
            ...s,
            clockOut: localNow,
            notes: notes,
          });
        } else {
          updatedList.push(s);
        }
      }

      setClockOutTime(localNow);
      setIsClockedIn(false);
      setShifts(updatedList);
      setNotes('');
    } catch (err) {
      console.error('Clock out failed:', err);
    }

    setShowPopup(false);
  }

  function tileContent({ date: tileDate, view }) {
    if (view !== 'month') return null;

    const dayStart = moment.tz(tileDate, localTZ).startOf('day');
    const dayEnd = moment.tz(tileDate, localTZ).endOf('day');

    let hasWork = false;
    let hasEvent = false;

    for (let i = 0; i < shifts.length; i++) {
      const shiftTime = shifts[i].clockIn;
      if (shiftTime.isBetween(dayStart, dayEnd, null, '[]')) {
        if (shifts[i].type === 'work') hasWork = true;
        if (shifts[i].type === 'event') hasEvent = true;
      }
    }

    const today = moment().tz(localTZ).format('YYYY-MM-DD');
    const formatted = dayStart.format('YYYY-MM-DD');

    let tileClassName = '';
    if (formatted < today && hasWork) {
      tileClassName = 'history-tile';
    } else if (formatted === today) {
      tileClassName = 'today-tile';
    } else if (hasWork) {
      tileClassName = 'booked-tile';
    } else if (hasEvent) {
      tileClassName = 'events-tile';
    }

    return tileClassName ? (
      <div className={tileClassName}>
        <abbr className="reserved-abbr" aria-label={formatted}>
          {tileDate.getDate()}
        </abbr>
      </div>
    ) : null;
  }

  return (
    <>
      <div className="container">
        <BackButton />
        <h4 className="clock-in-out-header">⏱️ Clock In/Out</h4>
        <h4 className="clock-in-out-message">
          Need help? Review our <a href="/console/sop/clockInOut">Clock-In SOP</a>
        </h4>

        <div className="clock-status">
          <p><strong>Status:</strong> {isClockedIn ? 'Clocked In' : 'Clocked Out'}</p>
          {clockInTime && (
            <p>
              <strong>Clock In:</strong>{' '}
              {moment.utc(clockInTime).tz(localTZ).format('MMM D, YYYY hh:mm A')}
            </p>
          )}
          {clockOutTime && (
            <p>
              <strong>Clock Out:</strong>{' '}
              {moment.utc(clockOutTime).tz(localTZ).format('MMM D, YYYY hh:mm A')}
            </p>
          )}
          {isClockedIn && (
            <div className="clock-note-entry">
              <textarea
                id="shiftNotes"
                value={notes}
                onChange={function (e) { setNotes(e.target.value); }}
                placeholder="Leave notes..."
              />
            </div>
          )}
        </div>

        <div className="clock-actions">
          <button
            className={`clock-in-button ${isClockedIn ? 'disabled' : ''}`}
            onClick={function () { setPopupType('in'); setShowPopup(true); }}
            disabled={isClockedIn}
          >
            Clock In
          </button>
          <button
            className={`clock-out-button ${!isClockedIn ? 'disabled' : ''}`}
            onClick={function () { setPopupType('out'); setShowPopup(true); }}
            disabled={!isClockedIn}
          >
            Clock Out
          </button>
        </div>

        <div className="calendar-container">
          <div className="legend">
            <div className="legend-item"><span className="history-dot" /> History</div>
            <div className="legend-item"><span className="today-dot" /> Today</div>
            <div className="legend-item"><span className="booked-dot" /> Booked</div>
            <div className="legend-item"><span className="events-dot" /> Events</div>
          </div>

          <Calendar
            onChange={function (d) { setDate(d); }}
            value={date}
            tileContent={tileContent}
            calendarType="gregory"
          />
        </div>

        <div className="event-details">
          <h4 className="clock-log-header">
            📋 Shift Log: {moment.tz(date, localTZ).format('MMM D, YYYY')}
          </h4>
          {selectedDateShifts.length > 0 ? (
            <div className="shift-table-wrapper">
              <table className="shift-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Clock In</th>
                    <th>Clock Out</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedDateShifts.map(function (shift, idx) {
                    const localClockIn = moment.utc(shift.clockIn).tz(localTZ);
                    const localClockOut = shift.clockOut ? moment.utc(shift.clockOut).tz(localTZ) : null;

                    return (
                      <tr key={idx}>
                        <td>{localClockIn.format('MMM D, YYYY')}</td>
                        <td>{localClockIn.format('hh:mm A')}</td>
                        <td>{localClockOut ? localClockOut.format('hh:mm A') : '-'}</td>
                        <td>{shift.notes || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="event-details-message">No shifts recorded for this day.</p>
          )}
        </div>

        {showPopup && (
          <ConfirmationPopup
            message={`Are you sure you want to clock ${popupType === 'in' ? 'in' : 'out'}?`}
            onConfirm={popupType === 'in' ? confirmClockIn : confirmClockOut}
            onCancel={function () { setShowPopup(false); }}
          />
        )}
      </div>
      <hr />
      <Footer />
    </>
  );
};

export default ClockInAndOut;
